import getQueryParamsFromHash from '@functions/utils/getQueryParamsFromHash'
import parsePermittedQueryParams from '@functions/utils/parsePermittedQueryParams'
import setQueryParamsToHash from '@functions/utils/setQueryParamsToHash'

import useDeepEffect from '@hooks/useDeepEffect'

import useSetState from '@hooks/useSetState'

type Filters = {
  [key: string]: any;
}

const saveQueryParamsToHash = (
  params: {
    defaultFilters: any,
    isReset?: boolean,
    pageFilters: any
    persistedExternalFilterKeys: string[],
  }
) => {
  const { defaultFilters, isReset = false, pageFilters, persistedExternalFilterKeys } = params

  const currentQueryParams = getQueryParamsFromHash()
  const currentQueryParamKeys = Object.keys(currentQueryParams).concat(persistedExternalFilterKeys)

  const defaultFilterKeys = Object.keys(defaultFilters)

  // Setup new params which remove query params that are already stored
  // as default filters but are being cleared
  const persistedParams = currentQueryParamKeys.reduce((acc: any, key) => {
    const isDefaultFilter = defaultFilterKeys.includes(key)

    // Hasn't been stored as default filter
    // or we are resetting back to default
    if (!isDefaultFilter || isReset){
      acc[key] = defaultFilters[key]
    }

    // If the key is an external filter, we always want to include it
    if (persistedExternalFilterKeys.includes(key)) {
      acc[key] = currentQueryParams[key]
    }

    // Has been stored as default filter and still has a value
    if (isDefaultFilter && pageFilters[key]){
      acc[key] = pageFilters[key]
    }

    return acc
  }, {})

  const queryParams = {
    ...persistedParams,
    ...pageFilters,
  }

  setQueryParamsToHash(queryParams)
}

type UpdateFiltersParams = {
  defaultFilters: Filters,
  filters: Filters,
  isReset?: boolean,
  persistFilters?: boolean,
  persistedExternalFilterKeys: string[],
  setState: (state: object) => void,
}

const updateFilters = (params: UpdateFiltersParams): void => {
  const {
    defaultFilters, filters, isReset, persistFilters, persistedExternalFilterKeys, setState,
  } = params

  const filterKeys = Object.keys(filters)
  const updatedFilters: Filters = {}
  const pageFilters: Filters = {}

  // Prevents overwriting of falsy values so that we can overwrite a default.
  Object.entries(defaultFilters).forEach(([key, value]) => {
    if (!filterKeys.includes(key)){
      updatedFilters[key] = value
      if (!persistedExternalFilterKeys.includes(key)) pageFilters[key] = value
    }
  })

  filterKeys.forEach((key) => {
    const value = filters[key]
    const valueIsPresent = value !== undefined && value !== null

    if (valueIsPresent) updatedFilters[key] = value
    if (valueIsPresent && !persistedExternalFilterKeys.includes(key)) pageFilters[key] = value
  })

  setState({ pageFilters: isReset ? defaultFilters : pageFilters })

  // Persist Filters in Hash
  if (persistFilters){
    setState({
      persistedFilters: pageFilters,
      defaultAndPersistedFilters: { ...defaultFilters, ...pageFilters },
    })

    saveQueryParamsToHash({
      defaultFilters,
      isReset,
      pageFilters: updatedFilters,
      persistedExternalFilterKeys: persistedExternalFilterKeys,
    })
  }
}

export type UseFiltersOptions = {
  defaultFilters?: object,
  persistFilters?: boolean,
  persistedExternalFilterKeys?: string[],
}

const setupDefaultPageFilters = (options: UseFiltersOptions) => {
  const { defaultFilters = {}, persistFilters = false, persistedExternalFilterKeys = [] } = options

  const persistedFilters = persistFilters ? getQueryParamsFromHash() : {}

  // remove any values from persistedFilters if the key is in persistedExternalFilterKeys
  Object.keys(persistedFilters).forEach((key) => {
    if (persistedExternalFilterKeys!.includes(key)) {
      delete persistedFilters[key]
    }
  })

  return {
    defaultFilters,
    mergedFilters: {
      ...defaultFilters,
      ...persistedFilters,
    },
    persistedFilters,
  }
}

const defaultState = {
  defaultAndPersistedFilters: {},
  defaultFilters: {},
  defaultFiltersModifiedAt: 0,
  pageFilters: {},
  persistedFilters: {},
  showFilters: false,
}

export type UseFiltersPayload = {
  callbacks: {
    resetFilters: (resetFn?: (defaultFilters?: object) => void) => void,
    toggleFilters: () => void,
    updateFilters: (filters: object) => void,
  },
  defaultAndPersistedFilters: object,
  defaultFilters: object,
  defaultFiltersModifiedAt: number,
  pageFilters: object,
  persistedExternalFilters: object,
  persistedFilters: object,
  showFilters: boolean,
}

function useFilters(options: UseFiltersOptions = {}): UseFiltersPayload {
  const { persistFilters = false, persistedExternalFilterKeys = [] } = options

  const [state, setState] = useSetState(defaultState)
  const {
    defaultAndPersistedFilters,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
    persistedFilters,
    showFilters,
  } = state

  useDeepEffect(() => {
    const filters = setupDefaultPageFilters(options)

    if (persistFilters) {
      saveQueryParamsToHash({
        defaultFilters: filters.defaultFilters,
        isReset: false,
        pageFilters: filters.mergedFilters,
        persistedExternalFilterKeys,
      })
    }

    setState({
      defaultAndPersistedFilters: filters.mergedFilters,
      defaultFilters: filters.defaultFilters,
      defaultFiltersModifiedAt: new Date().getTime(),
      pageFilters: filters.mergedFilters,
      persistedFilters: filters.persistedFilters,
    })
  }, [options.defaultFilters])

  const persistedExternalFilters = parsePermittedQueryParams(getQueryParamsFromHash(), persistedExternalFilterKeys || [])

  return {
    callbacks: {
      resetFilters: (resetFn) => {
        if (resetFn) resetFn()

        updateFilters({
          defaultFilters,
          filters: {},
          isReset: true,
          persistFilters,
          persistedExternalFilterKeys,
          setState,
        })
      },
      toggleFilters: () => setState({ showFilters: !showFilters }),
      updateFilters: (filters) => updateFilters({
        defaultFilters: { ...defaultFilters, ...getQueryParamsFromHash() },
        filters,
        persistFilters,
        persistedExternalFilterKeys,
        setState,
      }),
    },
    defaultAndPersistedFilters,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
    persistedExternalFilters,
    persistedFilters,
    showFilters,
  }
}

export default useFilters
